import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LocationSummary, PaymentMethod, StockRow } from '@teo/shared';
import { api } from '../api/client';
import { useToast } from '../lib/useToast';
import { formatMoney, formatNumber } from '../lib/format';
import { swatchColor } from '../lib/colors';
import type { Language } from '../i18n';

/** Línea de un carrito (venta, despacho). El precio es editable cuando la operación lo permite. */
export interface CartLine {
  variant_id: number;
  ref: string;
  product_name: string;
  color: string | null;
  size: string | null;
  qty: number;
  unit_price: number;
  suggested: number;
  min_price: number;
  available: number;
}

/** Muestra de color de la variante (neutra y rayada cuando el color no se reconoce o el producto no lo usa). */
export function Swatch({ color }: { color: string | null }) {
  const hex = swatchColor(color);
  return <span className={hex ? 'swatch' : 'swatch none'} style={hex ? { background: hex } : undefined} data-swatch={hex ?? ''} aria-hidden="true" />;
}

export function variantLabel(v: { color: string | null; size: string | null }): string {
  return [v.color, v.size].filter(Boolean).join(' / ');
}

export const cartTotal = (lines: CartLine[]) => lines.reduce((s, l) => s + l.qty * l.unit_price, 0);
export const cartUnits = (lines: CartLine[]) => lines.reduce((s, l) => s + l.qty, 0);

/** Agrega la variante al carrito o suma una unidad si ya estaba (sin pasar de lo disponible). */
export function addToCart(lines: CartLine[], row: StockRow, price: number): CartLine[] {
  const found = lines.find((l) => l.variant_id === row.variant_id);
  if (found) return lines.map((l) => (l === found ? { ...l, qty: Math.min(l.qty + 1, l.available) } : l));
  return [
    ...lines,
    {
      variant_id: row.variant_id, ref: row.ref, product_name: row.product_name, color: row.color, size: row.size, qty: 1,
      unit_price: price, suggested: price, min_price: row.min_sale_price ?? 0, available: row.qty,
    },
  ];
}

/** Selector de ubicación: solo se muestra cuando el usuario puede operar en más de una. */
export function LocationSelect({ id, locations, value, onChange }: { id: string; locations: LocationSummary[]; value: number | null; onChange: (id: number) => void }) {
  if (locations.length <= 1) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <select className="select" id={id} style={{ width: '100%' }} value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))}>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Buscador del prototipo (.searchline + .results) sobre las existencias de una ubicación. */
export function StockSearch({
  idPrefix, locationId, placeholder, onPick, onlyAvailable = true,
}: {
  idPrefix: string;
  locationId: number | null;
  placeholder: string;
  onPick: (row: StockRow) => void;
  onlyAvailable?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { showError } = useToast();
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<StockRow[] | null>(null);

  const search = () => {
    if (!locationId || !term.trim()) return setRows(null);
    api.inventory({ location_id: locationId, q: term.trim(), only_available: onlyAvailable, limit: 40 }).then(setRows).catch(showError);
  };

  return (
    <>
      <div className="searchline">
        <input
          id={`${idPrefix}Search`}
          placeholder={placeholder}
          value={term}
          autoComplete="off"
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              search();
            }
          }}
        />
        <button type="button" className="btn" id={`${idPrefix}Find`} onClick={search}>
          {t('ops.find')}
        </button>
      </div>
      <div className="results" id={`${idPrefix}Results`}>
        {rows?.map((r) => (
          <div className="result" key={r.variant_id} data-variant={r.variant_id}>
            <Swatch color={r.color} />
            <div className="resulttext">
              <b>{t('ops.refName', { ref: r.ref, name: r.product_name })}</b>
              <small>{[variantLabel(r), t('ops.available', { n: formatNumber(r.qty, lang) })].filter(Boolean).join(' · ')}</small>
            </div>
            <button type="button" onClick={() => onPick(r)}>
              {t('common.add')}
            </button>
          </div>
        ))}
        {rows && rows.length === 0 ? <div className="empty">{t('ops.notFound')}</div> : null}
      </div>
    </>
  );
}

/** Campo numérico que deja borrar y reescribir: solo confirma valores válidos y los ajusta al rango permitido. */
function NumField({ value, min, max, field, disabled, onCommit }: { value: number; min: number; max?: number; field: string; disabled?: boolean; onCommit: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      type="number" min={min} max={max} value={text} data-field={field} disabled={disabled}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() !== '' && Number.isFinite(n) && n >= min) onCommit(max == null ? n : Math.min(n, max));
      }}
      onBlur={() => setText(String(value))}
    />
  );
}

/** Carrito del prototipo (.cartline): cantidad y, si aplica, precio acordado editable. */
export function CartLines({
  idPrefix, lines, onChange, editablePrice, priceLabel,
}: {
  idPrefix: string;
  lines: CartLine[];
  onChange: (lines: CartLine[]) => void;
  editablePrice: boolean;
  priceLabel: string;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const patch = (id: number, p: Partial<CartLine>) => onChange(lines.map((l) => (l.variant_id === id ? { ...l, ...p } : l)));

  return (
    <div className="cart" id={`${idPrefix}Cart`}>
      {lines.length === 0 ? <div className="empty">{t('ops.cartEmpty')}</div> : null}
      {lines.map((l) => {
        const below = l.min_price > 0 && l.unit_price < l.min_price;
        return (
          <div className="cartline" key={l.variant_id} data-variant={l.variant_id}>
            <div>
              <b><Swatch color={l.color} /> {t('ops.refName', { ref: l.ref, name: l.product_name })}</b>
              <small>
                {[
                  variantLabel(l),
                  t('ops.suggested', { price: formatMoney(l.suggested, lang) }),
                  l.unit_price !== l.suggested ? t('ops.negotiated') : '',
                  t('ops.available', { n: formatNumber(l.available, lang) }),
                ].filter(Boolean).join(' · ')}
              </small>
              {below ? <div className="pricewarning">{t('ops.belowMin', { price: formatMoney(l.min_price, lang) })}</div> : null}
              <button type="button" className="btn sm" style={{ marginTop: 6 }} onClick={() => onChange(lines.filter((x) => x.variant_id !== l.variant_id))}>
                {t('common.remove')}
              </button>
            </div>
            <div className="linecontrols">
              <div className="miniField">
                <label>{t('ops.qty')}</label>
                <NumField value={l.qty} min={1} max={l.available} field="qty" onCommit={(n) => patch(l.variant_id, { qty: Math.floor(n) })} />
              </div>
              <div className="miniField">
                <label>{priceLabel}</label>
                <NumField value={l.unit_price} min={0} field="price" disabled={!editablePrice} onCommit={(n) => patch(l.variant_id, { unit_price: n })} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export type PayAmounts = Record<number, string>;

export const paidTotal = (amounts: PayAmounts) => Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);

/** Pagos de la venta listos para la API (solo los que tienen valor). */
export function paymentsFrom(amounts: PayAmounts): { payment_method_id: number; amount: number }[] {
  return Object.entries(amounts)
    .map(([id, v]) => ({ payment_method_id: Number(id), amount: Number(v) || 0 }))
    .filter((p) => p.amount > 0);
}

/** Pagos mixtos del prototipo (.paygrid) con los medios de pago configurados por Gerencia. */
export function PayGrid({
  idPrefix, methods, total, amounts, onChange, customer, onCustomer,
}: {
  idPrefix: string;
  methods: PaymentMethod[];
  total: number;
  amounts: PayAmounts;
  onChange: (amounts: PayAmounts) => void;
  customer: string;
  onCustomer: (name: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const active = methods.filter((m) => m.active);
  const paid = paidTotal(amounts);
  const diff = Math.round(total - paid);
  const credit = active.filter((m) => m.kind === 'DIRECT_CREDIT').some((m) => (Number(amounts[m.id]) || 0) > 0);
  let status: ReactNode = t('ops.payComplete');
  if (diff > 0) status = t('ops.payMissing', { amount: formatMoney(diff, lang) });
  if (diff < 0) status = t('ops.payExcess', { amount: formatMoney(-diff, lang) });

  return (
    <>
      <div className="paygrid" id={`${idPrefix}PayGrid`}>
        {active.map((m) => (
          <div className="paybox" key={m.id}>
            <label htmlFor={`${idPrefix}Pay-${m.code}`}>{m.name}</label>
            <input id={`${idPrefix}Pay-${m.code}`} type="number" min={0} value={amounts[m.id] ?? ''} placeholder="0" onChange={(e) => onChange({ ...amounts, [m.id]: e.target.value })} />
          </div>
        ))}
      </div>
      {credit ? (
        <div className="field">
          <label htmlFor={`${idPrefix}Customer`}>{t('ops.creditCustomer')}</label>
          <input id={`${idPrefix}Customer`} value={customer} onChange={(e) => onCustomer(e.target.value)} />
        </div>
      ) : null}
      <div className={diff === 0 ? 'paystatus' : 'paystatus bad'} id={`${idPrefix}PayStatus`}>
        {status}
      </div>
      <div className="actions">
        {active.filter((m) => ['CASH', 'TRANSFER', 'CARD'].includes(m.kind)).map((m) => (
          <button type="button" className="btn sm" key={m.id} data-payall={m.code} onClick={() => onChange({ [m.id]: String(total) })}>
            {t('ops.payAll', { name: m.name })}
          </button>
        ))}
      </div>
    </>
  );
}
